import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, FormikProps, FieldArray } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MuiTextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import LoadingButton from '@mui/lab/LoadingButton'

import CheckboxField from '@/components/formik/fields/CheckboxField'
import TextField from '@/components/formik/fields/TextField'

export type EmailDocumentsFormValues = {
  emails: string[]
  suggested_emails: string[]
}

export type EmailsDocumentsDialogProps = {
  emailOptions: string[]
  isSending?: boolean
  onClose?: () => void
  onSend: (emails: string[], pdfPassword?: string) => void
  open: boolean
}

export function EmailDocumentsDialog(props: EmailsDocumentsDialogProps) {
  const { emailOptions, isSending = false, onClose, open, onSend } = props
  const { t } = useTranslation()

  const [passwordEnabled, setPasswordEnabled] = useState(false)
  const [pdfPassword, setPdfPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const emailsDocumentsSchema = useMemo(
    () =>
      yup.object().shape({
        emails: yup.array().of(
          yup
            .string()
            .email()
            .required(t('field.email.error', { context: 'required' }))
        ),
      }),
    [t]
  )

  const handleSubmit = (values: EmailDocumentsFormValues) => {
    onSend(
      [...values.emails, ...values.suggested_emails],
      passwordEnabled && pdfPassword ? pdfPassword : undefined
    )
  }

  const handleClose = () => {
    setPasswordEnabled(false)
    setPdfPassword('')
    setShowPassword(false)
    onClose?.()
  }

  return (
    <Dialog onClose={handleClose} open={open} PaperProps={{ sx: { width: '30%' } }}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 'bold' }}>{t('page.liveConsultation.emailDocumentsDialog.title')}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              transform: 'translateX(25%)',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Formik initialValues={{ emails: [], suggested_emails: [] }} onSubmit={handleSubmit} validationSchema={emailsDocumentsSchema}>
          {({ values }: FormikProps<EmailDocumentsFormValues>) => (
            <Form>
              <Stack spacing={3}>
                <FormGroup>
                  {emailOptions.map((email, index) => (
                    <CheckboxField key={`${email}-${index}`} label={email} name="suggested_emails" value={email} />
                  ))}
                </FormGroup>
                <FieldArray
                  name="emails"
                  render={(arrayHelpers) => (
                    <Stack justifyContent="center" spacing={3}>
                      {values.emails.map((_email: string, index: number) => (
                        <Box key={index}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <TextField fullWidth name={`emails.${index}`} />
                            <IconButton onClick={() => arrayHelpers.remove(index)}>
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Stack>
                        </Box>
                      ))}
                      <Button onClick={() => arrayHelpers.insert(values.emails.length, '')}>
                        {t('page.liveConsultation.emailDocumentsDialog.addEmailButton.label')}
                      </Button>
                    </Stack>
                  )}
                />
                {/* TODO: In the future, add option to send the password via SMS or a separate email */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={passwordEnabled}
                      onChange={(e) => {
                        setPasswordEnabled(e.target.checked)
                        if (!e.target.checked) {
                          setPdfPassword('')
                          setShowPassword(false)
                        }
                      }}
                    />
                  }
                  label={t('page.liveConsultation.emailDocumentsDialog.pdfPassword.toggle')}
                />
                <Collapse in={passwordEnabled}>
                  <MuiTextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label={t('page.liveConsultation.emailDocumentsDialog.pdfPassword.label')}
                    value={pdfPassword}
                    onChange={(e) => setPdfPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Collapse>
                <Stack alignItems="center">
                  <LoadingButton
                    disabled={!(values.emails.length + values.suggested_emails.length) || (passwordEnabled && !pdfPassword)}
                    loading={isSending}
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    {t('common:button.send.label')}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EmailDocumentsDialog
